/**
 * Developer Teammates tab — lists all teammates from the v2 API.
 *
 * Features: copy-to-clipboard on IDs, hover tooltips on truncated cells.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { CopyCell } from "@/components/ui/copy-cell";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { env } from "@/env";
import { getAccessToken } from "@/lib/auth/storage";

export const Route = createFileRoute("/_dashboard/developer/teammates")({
	component: DeveloperTeammatesPage,
});

function useV2Teammates() {
	return useQuery({
		queryKey: ["developer", "teammates"],
		queryFn: async () => {
			const token = getAccessToken();
			const res = await fetch(`${env.VITE_API_BASE_URL}/api/v2/teammates/`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!res.ok) throw new Error(`Failed to fetch teammates: ${res.status}`);
			const body = await res.json();
			return body.data;
		},
		staleTime: 30_000,
	});
}

function DeveloperTeammatesPage() {
	const { data: teammates, isLoading, isError } = useV2Teammates();

	if (isError) {
		return <div className="py-16 text-center text-destructive">Failed to load teammates</div>;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
			</div>
		);
	}

	if (!teammates?.length) {
		return <div className="py-16 text-center text-muted-foreground">No teammates found</div>;
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>ID</TableHead>
					<TableHead>Name</TableHead>
					<TableHead>Tools</TableHead>
					<TableHead>User ID</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Created</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{teammates.map((t: Record<string, unknown>) => (
					<TableRow key={String(t.id)}>
						<TableCell>
							<CopyCell value={String(t.id)} />
						</TableCell>
						<TableCell className="font-medium max-w-xs truncate" title={String(t.name)}>
							{String(t.name)}
						</TableCell>
						<TableCell>
							<div className="flex flex-wrap gap-1">
								{(t.tools as string[] | undefined)?.map((tool) => (
									<Badge key={tool} variant="secondary" className="text-xs">
										{tool}
									</Badge>
								))}
							</div>
						</TableCell>
						<TableCell className="text-muted-foreground">
							{t.user_id ? String(t.user_id) : "—"}
						</TableCell>
						<TableCell>
							<Badge variant={t.status === "enabled" ? "default" : "secondary"}>
								{String(t.status)}
							</Badge>
						</TableCell>
						<TableCell className="text-muted-foreground text-xs">
							{new Date(String(t.created_at)).toLocaleDateString()}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
